import { useForm, useValidator } from "@/hooks";
import {
    Autocomplete,
    Button,
    DateRangePicker,
    FormLayout,
    Heading,
    Input,
    Select,
    Switch,
    Textarea,
    TimePicker,
} from "@/ui";
import { format, isValid, parse, startOfDay } from "date-fns";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";

import { RESERVATION_MODE_OPTIONS } from "config/constant";
import styles from "./Closures.module.scss";

const DATE_FMT = "MM/dd/yyyy";

const parseClosureDate = value => {
    if (!value) return undefined;
    const parsed = parse(value, DATE_FMT, new Date());
    return isValid(parsed) ? parsed : undefined;
};

const ClosureForm = ({
    isCreate,
    handleCancel,
    closures,
    selectClosure,
    isUpdating,
    handlerUpdatingOrCreate,
    stores,
}) => {
    const [isFormReset, resetFormInputs] = useState(isCreate);
    const { inputs, selectChange, inputChange, setInputs } = useForm();

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "name",
                method: "isEmpty",
                validWhen: false,
                message: "The Name is required.",
            },
            {
                field: "note",
                method: "isEmpty",
                validWhen: false,
                message: "The Note is required.",
            },
            {
                field: "reservation_mode",
                method: "isEmpty",
                validWhen: false,
                message: "The Reservation mode is required.",
            },
            {
                field: "stores_provider_ids",
                method: "isEmpty",
                validWhen: false,
                message: "The Store provider id is required.",
            },
            {
                field: "start_date",
                method: "isEmpty",
                validWhen: false,
                message: "The Start date is required.",
            },
        ],
        {
            ...inputs,
        },
    );

    const reservationModeOptions = useMemo(
        () =>
            (RESERVATION_MODE_OPTIONS || []).map(op => ({
                value: String(op.value),
                label: op.label,
            })),
        [],
    );

    const storeOptions = useMemo(
        () =>
            (stores || []).map(store => ({
                value: String(store.value),
                label: store.label,
            })),
        [stores],
    );

    useEffect(() => {
        if (!stores?.length) return;

        if (isCreate) {
            setInputs(prev => ({ ...prev, all_day: true }));
        } else {
            const selected = closures?.[selectClosure];
            if (selected) {
                setInputs({
                    name: selected.name || "",
                    note: selected.note || "",
                    stores_provider_ids: selected.stores_provider_ids || [],
                    all_day: selected.all_day || false,
                    start_date: selected.start_date,
                    end_date: selected.end_date,
                    time_start: selected.time_start,
                    time_end: selected.time_end,
                    reservation_mode: selected.reservation_mode,
                });
            }
        }
    }, [isCreate, closures, selectClosure, stores]);

    useEffect(() => {
        if (!isCreate && !isFormReset) {
            resetFormInputs(true);
        }
    }, [isCreate, isFormReset]);

    const handleStoreChange = ids => {
        const selectedIds = (ids ?? []).map(id => {
            const store = stores?.find(s => String(s.value) === String(id));
            return store ? store.value : id;
        });
        setInputs(prev => ({ ...prev, stores_provider_ids: selectedIds }));
    };

    const storeSelectedValues = useMemo(() => {
        const ids = Array.isArray(inputs?.stores_provider_ids)
            ? inputs.stores_provider_ids
            : [];
        return ids.map(String);
    }, [inputs?.stores_provider_ids]);

    const handleDateChange = range => {
        setInputs(prev => ({
            ...prev,
            start_date: range?.from ? format(range.from, DATE_FMT) : null,
            end_date: range?.to ? format(range.to, DATE_FMT) : null,
        }));
    };

    const handleTimeChange = (field, value) => {
        setInputs(prev => ({ ...prev, [field]: value }));
    };

    const toggleAllDay = checked => {
        setInputs(prev => ({
            ...prev,
            all_day: checked,
            ...(checked ? { time_start: null, time_end: null } : {}),
        }));
    };

    const handleClose = () => {
        handleCancel();
        resetFormInputs(false);
    };

    const handleSave = () => {
        if (isSubmited) isSubmited(true);
        if (!isValid()) return;

        const newClosure = {
            name: inputs.name,
            note: inputs.note,
            stores_provider_ids: inputs.stores_provider_ids,
            all_day: inputs.all_day,
            start_date: inputs.start_date,
            end_date: inputs.end_date,
            time_start: inputs.time_start,
            time_end: inputs.time_end,
            reservation_mode: inputs.reservation_mode,
        };

        const updatedClosures = _.cloneDeep(closures ?? []);
        if (isCreate) {
            updatedClosures.push(newClosure);
        } else {
            updatedClosures[selectClosure] = newClosure;
        }

        handlerUpdatingOrCreate(updatedClosures);
    };

    const fieldError = field =>
        validator[field]?.isInvalid ? validator[field]?.message : undefined;

    return (
        <div className={styles.formPanel}>
            <FormLayout>
                <Heading level={5}>
                    {isCreate ? "Add Closure" : "Update Closure"}
                </Heading>
                <FormLayout.Section title="Informations">
                    <FormLayout.Grid columns={2} maxWidth="twoThirds">
                        <FormLayout.Full>
                            <Input
                                error={fieldError("name")}
                                fullWidth
                                label="Name"
                                name="name"
                                onChange={inputChange}
                                placeholder="Name"
                                value={inputs.name ?? ""}
                            />
                        </FormLayout.Full>
                        <Select
                            error={fieldError("reservation_mode")}
                            fullWidth
                            label="Reservation Mode"
                            onValueChange={value =>
                                selectChange({ value }, "reservation_mode")
                            }
                            options={reservationModeOptions}
                            value={
                                inputs.reservation_mode != null
                                    ? String(inputs.reservation_mode)
                                    : undefined
                            }
                        />
                        <Autocomplete
                            error={fieldError("stores_provider_ids")}
                            fullWidth
                            label="Store Provider Id"
                            maxVisibleChips={1}
                            multiple
                            name="stores_provider_ids"
                            onChange={handleStoreChange}
                            options={storeOptions}
                            value={storeSelectedValues}
                        />
                        <FormLayout.Full>
                            <Textarea
                                error={fieldError("note")}
                                fullWidth
                                label="Note"
                                name="note"
                                onChange={inputChange}
                                placeholder="Write the notes..."
                                rows={5}
                                value={inputs.note ?? ""}
                            />
                        </FormLayout.Full>
                    </FormLayout.Grid>
                </FormLayout.Section>

                <FormLayout.Section title="Closing period">
                    <FormLayout.Grid columns={2} maxWidth="twoThirds">
                        <DateRangePicker
                            error={fieldError("start_date")}
                            fromDate={startOfDay(new Date())}
                            fullWidth
                            label="Date"
                            onChange={handleDateChange}
                            value={{
                                from: parseClosureDate(inputs.start_date),
                                to: parseClosureDate(inputs.end_date),
                            }}
                        />
                        <Switch
                            checked={!!inputs.all_day}
                            label="All Day"
                            name="all_day"
                            onCheckedChange={toggleAllDay}
                        />
                        {!inputs.all_day ? (
                            <FormLayout.Full>
                                <FormLayout.Grid columns={2}>
                                    <TimePicker
                                        fullWidth
                                        label="Time start"
                                        name="time_start"
                                        onChange={value =>
                                            handleTimeChange(
                                                "time_start",
                                                value,
                                            )
                                        }
                                        value={inputs.time_start ?? ""}
                                    />
                                    <TimePicker
                                        fullWidth
                                        label="Time end"
                                        name="time_end"
                                        onChange={value =>
                                            handleTimeChange("time_end", value)
                                        }
                                        value={inputs.time_end ?? ""}
                                    />
                                </FormLayout.Grid>
                                <p className={styles.timeHint}>
                                    <strong>Time start:</strong> It is the time
                                    of day when closing begins.
                                    <br />
                                    <strong>Time end:</strong> It is the time of
                                    day when the closure ends.
                                </p>
                            </FormLayout.Full>
                        ) : null}
                    </FormLayout.Grid>
                </FormLayout.Section>

                <FormLayout.Section>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 12,
                        }}
                    >
                        <Button
                            disabled={isUpdating}
                            onClick={handleClose}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                        <Button
                            loading={isUpdating}
                            onClick={handleSave}
                            type="button"
                            variant="primary"
                        >
                            Save
                        </Button>
                    </div>
                </FormLayout.Section>
            </FormLayout>
        </div>
    );
};

export default ClosureForm;
